import classNames from 'classnames';
import styles from './DeliveryStepper.module.scss';

type Props = {
  stopsBeforeCount: number;
  eta?: string;
};

function DeliveryStepper({stopsBeforeCount, eta}: Props) {
  const isArrivingNow = stopsBeforeCount === 0;
  const totalStops = stopsBeforeCount + 1;
  const currentStopIndex = 0;

  // When stopsBeforeCount is 0, show a simplified "arriving now" UI
  if (isArrivingNow) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Your Delivery</h3>
          {eta && <span className={styles.eta}>ETA: {eta}</span>}
        </div>

        <div className={styles.arrivingNowContent}>
          <div className={styles.arrivingNowIcon}>
            <div className={styles.truckIconWrapper}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 8H17V4H3C1.9 4 1 4.9 1 6V17H3C3 18.66 4.34 20 6 20C7.66 20 9 18.66 9 17H15C15 18.66 16.34 20 18 20C19.66 20 21 18.66 21 17H23V12L20 8ZM19.5 9.5L21.46 12H17V9.5H19.5ZM6 18C5.45 18 5 17.55 5 17C5 16.45 5.45 16 6 16C6.55 16 7 16.45 7 17C7 17.55 6.55 18 6 18ZM18 18C17.45 18 17 17.55 17 17C17 16.45 17.45 16 18 16C18.55 16 19 16.45 19 17C19 17.55 18.55 18 18 18Z"
                  fill="currentColor"
                />
              </svg>
              <div className={styles.truckPulse} />
            </div>
          </div>

          <div className={styles.arrivingNowText}>
            <span className={styles.arrivingNowTitle}>Arriving Now</span>
            <span className={styles.arrivingNowSubtitle}>
              Your delivery is next in line
            </span>
          </div>
        </div>

        <div className={styles.arrivingNowBadge}>
          <span className={styles.badgeIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L4 5V11.09C4 16.14 7.41 20.85 12 22C16.59 20.85 20 16.14 20 11.09V5L12 2ZM10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className={styles.badgeText}>You're next!</span>
        </div>
      </div>
    );
  }

  // When there are stops before, show the stepper UI
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Delivery Progress</h3>
        {eta && <span className={styles.eta}>ETA: {eta}</span>}
      </div>

      <div className={styles.stepperWrapper}>
        <div className={styles.stepper}>
          {Array.from({length: totalStops}).map((_, index) => {
            const isCompleted = index < currentStopIndex;
            const isCurrent = index === stopsBeforeCount;
            const isUpcoming =
              index > currentStopIndex && index < stopsBeforeCount;

            return (
              <div
                key={index}
                className={classNames(styles.step, {
                  [styles.completed]: isCompleted,
                  [styles.current]: isCurrent,
                  [styles.upcoming]: isUpcoming,
                })}
              >
                <div className={styles.stepDot}>
                  {isCurrent ? (
                    <div className={styles.currentDot}>
                      <div className={styles.pulsingRing} />
                      <div className={styles.innerDot} />
                    </div>
                  ) : (
                    <span className={styles.stepNumber}>{index + 1}</span>
                  )}
                </div>
                {index < totalStops - 1 && (
                  <div
                    className={classNames(styles.connector, {
                      [styles.completedConnector]: isCompleted,
                    })}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.labels}>
          <div className={styles.labelStart}>
            <span className={styles.stopsLabel}>
              {stopsBeforeCount} {stopsBeforeCount === 1 ? 'stop' : 'stops'}{' '}
              before yours
            </span>
          </div>
          <div className={styles.labelEnd}>
            <span className={styles.yourDelivery}>Your Delivery</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryStepper;
